import {otsBaseApi} from "../../ots-api";
import {Vehicle} from "../../../model/vehicle";
import {removeUndefined} from "../../../util/util";
import {FleetAssetCapability, FleetAssetCapabilityRequestDTO} from "../../../model/fleet-asset-capability";

const ALL_TAG_TYPES = [
  "Vehicles",
  "VehicleCapabilities",
  "ObjectObjectChangeLogs"
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const vehicleApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllVehicles: builder.query<Vehicle[], string | undefined>({
      query: (regionId) => ({
        url: `/vehicle/all`,
        method: "GET",
        params: regionId ? {regionId: regionId} : undefined
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Vehicles", id} as const)),
            {type: "Vehicles", id: "LIST"}
          ];
        } else {
          return [{type: "Vehicles", id: "LIST"}];
        }
      }
    }),
    getAllOperatorVehicles: builder.query<Vehicle[], string>({
      query: (regionId) => ({
        url: `/vehicle/operator/all`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId,
          withState: true
        })
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Vehicles", id} as const)),
            {type: "Vehicles", id: "LIST"}
          ];
        } else {
          return [{type: "Vehicles", id: "LIST"}];
        }
      }
    }),
    getVehicle: builder.query<Vehicle, string>({
      query: (id) => ({
        url: `/vehicle/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "Vehicles", id}]
    }),
    createVehicle: builder.mutation<Vehicle, Partial<Vehicle>>({
      query: (vehicle) => ({
        url: '/vehicle',
        method: 'POST',
        body: vehicle,
      }),
      invalidatesTags: [{type: 'Vehicles', id: 'LIST'}],
    }),
    updateVehicle: builder.mutation<Vehicle, Partial<Vehicle>>({
      query: (vehicle) => ({
        url: '/vehicle',
        method: 'PUT',
        body: vehicle,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: 'Vehicles', id},
        {type: 'ObjectObjectChangeLogs', id}
      ],
    }),
    deleteVehicle: builder.mutation<void, Partial<Vehicle>>({
      query: (vehicle) => ({
        url: '/vehicle',
        method: 'DELETE',
        body: vehicle,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Vehicles', id}],
    }),
    getVehicleCapabilities: builder.query<FleetAssetCapability[], string>({
      query: (vehicleId) => ({
        url: `/vehicles/${vehicleId}/capabilities`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => {
        return [
          {type: "VehicleCapabilities", id: arg}
        ];
      }
    }),
    addCapabilitiesToVehicle: builder.mutation<void, { vehicleId: string, data: FleetAssetCapabilityRequestDTO }>({
      query: (arg) => ({
        url: `/vehicles/${arg.vehicleId}/capabilities`,
        method: "POST",
        body: arg.data,
      }),
      invalidatesTags: (result, error, {vehicleId}) => [{type: "VehicleCapabilities", id: vehicleId}]
    }),
    removeCapabilitiesFromVehicle: builder.mutation<void, { vehicleId: string, data: FleetAssetCapabilityRequestDTO }>({
      query: (arg) => ({
        url: `/vehicles/${arg.vehicleId}/capabilities`,
        method: "DELETE",
        body: arg.data,
      }),
      invalidatesTags: (result, error, {vehicleId}) => [{type: "VehicleCapabilities", id: vehicleId}]
    }),
  })
});

export const {
  useGetAllVehiclesQuery,
  useGetAllOperatorVehiclesQuery,
  useGetVehicleQuery,
  useGetVehicleCapabilitiesQuery,
  useAddCapabilitiesToVehicleMutation,
  useRemoveCapabilitiesFromVehicleMutation,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useDeleteVehicleMutation,
} = vehicleApi;
